import { useQuery, useMutation } from '@tanstack/react-query'

// v1 Express server (/api) - for legacy endpoints
// v2 Laravel server (/api/v2) - for IG profile/media/comments (supports IG Login)
const V1 = '/api'
const V2 = '/api/v2'

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function v1get<T>(path: string): Promise<T> {
  const res = await fetch(`${V1}${path}`, { headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v2get<T>(path: string): Promise<T> {
  const res = await fetch(`${V2}${path}`, { headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v1post<T>(path: string, data?: unknown): Promise<T> {
  const res = await fetch(`${V1}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: data ? JSON.stringify(data) : undefined,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v1put<T>(path: string, data?: unknown): Promise<T> {
  const res = await fetch(`${V1}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: data ? JSON.stringify(data) : undefined,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v1del<T>(path: string): Promise<T> {
  const res = await fetch(`${V1}${path}`, { method: 'DELETE', headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

// Fetch accounts from v1 (Express) + v2-only accounts from Laravel (IG Login + OAuth v2)
export function useV1Accounts() {
  return useQuery<Array<{ igUserId: string; pageId: string; name: string; username: string; fbOnly: boolean; active: boolean; igLogin?: boolean }>>({
    queryKey: ['v1-accounts'],
    queryFn: async () => {
      const [v1Accounts, v2Accounts] = await Promise.all([
        v1get<Array<Record<string, unknown>>>('/accounts').catch(() => [] as Array<Record<string, unknown>>),
        fetch('/api/v2/accounts', { headers: authHeaders() })
          .then(r => r.ok ? r.json() : [])
          .catch(() => []),
      ])
      // Build v2 lookup for enrichment (tags, etc.)
      const v2ByIg = new Map<string, Record<string, unknown>>()
      const v2ByPage = new Map<string, Record<string, unknown>>()
      for (const acc of v2Accounts) {
        if (acc.igUserId) v2ByIg.set(String(acc.igUserId), acc)
        if (acc.pageId) v2ByPage.set(String(acc.pageId), acc)
      }

      // Enrich v1 accounts with v2 data (tags)
      const v1Ids = new Set(v1Accounts.map((a: Record<string, unknown>) => String(a.igUserId)))
      const v1PageIds = new Set(v1Accounts.map((a: Record<string, unknown>) => String(a.pageId)))
      const merged = v1Accounts.map((a: Record<string, unknown>) => {
        const v2 = v2ByIg.get(String(a.igUserId)) || v2ByPage.get(String(a.pageId))
        if (v2) {
          return { ...a, tags: v2.tags ?? a.tags }
        }
        return a
      })

      // Add v2-only accounts (IG Login, OAuth v2, etc.)
      for (const acc of v2Accounts) {
        if (!v1Ids.has(String(acc.igUserId)) && !v1PageIds.has(String(acc.pageId))) {
          merged.push(acc)
        }
      }
      return merged as Array<{ igUserId: string; pageId: string; name: string; username: string; fbOnly: boolean; active: boolean; igLogin?: boolean }>
    },
  })
}

export interface IGProfile {
  id: string
  username: string
  name: string
  biography: string
  followers_count: number
  follows_count: number
  media_count: number
  profile_picture_url: string
}

export interface IGMedia {
  id: string
  caption?: string
  media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM'
  media_url?: string
  thumbnail_url?: string
  timestamp: string
  like_count: number
  comments_count: number
}

export interface IGComment {
  id: string
  text: string
  timestamp: string
  from?: { id: string; username: string }
  like_count: number
}

export function useIGProfile(igUserId: string | undefined) {
  return useQuery<IGProfile>({
    queryKey: ['ig-profile', igUserId],
    queryFn: () => v2get(`/instagram/${igUserId}/profile`),
    enabled: !!igUserId,
  })
}

export function useIGMedia(igUserId: string | undefined) {
  return useQuery<{ data: IGMedia[] }>({
    queryKey: ['ig-media', igUserId],
    queryFn: () => v2get(`/instagram/${igUserId}/media?limit=20`),
    enabled: !!igUserId,
  })
}

export function useIGComments(igUserId: string | undefined, mediaId: string | undefined) {
  return useQuery<{ data: IGComment[] }>({
    queryKey: ['ig-comments', igUserId, mediaId],
    queryFn: () => v2get(`/instagram/${igUserId}/comments/${mediaId}`),
    enabled: !!igUserId && !!mediaId,
  })
}

export function useAddIGComment() {
  return useMutation({
    mutationFn: ({ igUserId, mediaId, message }: { igUserId: string; mediaId: string; message: string }) =>
      v1post(`/instagram/${igUserId}/comments/${mediaId}`, { message }),
  })
}

export function useEditIGComment() {
  return useMutation({
    mutationFn: ({ igUserId, commentId, message }: { igUserId: string; commentId: string; message: string }) =>
      v1put(`/instagram/${igUserId}/comments/${commentId}`, { message }),
  })
}

export function useDeleteIGComment() {
  return useMutation({
    mutationFn: ({ igUserId, commentId }: { igUserId: string; commentId: string }) =>
      v1del(`/instagram/${igUserId}/comments/${commentId}`),
  })
}

export function useSendInstagramDM() {
  return useMutation({
    mutationFn: (data: { igUserId: string; recipientId: string; message: string }) =>
      v1post<{ ok: boolean; messageId: string }>('/chat/send-instagram', data),
  })
}

export function useDMReplyToComment() {
  return useMutation({
    mutationFn: (data: { igUserId: string; commentId: string; message: string }) =>
      v1post<{ ok: boolean; recipientId?: string; messageId?: string }>(`/instagram/${data.igUserId}/dm-reply`, { commentId: data.commentId, message: data.message }),
  })
}
